import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, Row, Col, Input, Button, Form, Modal, ModalBody, Table, Tooltip } from 'reactstrap';
import { FaPlus, FaCheck, FaTrash, FaArrowLeft, FaGripVertical } from 'react-icons/fa';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import LoadingOverlay from "react-loading-overlay-ts";
import DNALoader from "../../../components/Loaders/DnaLoader";
import Alerts from "../../../components/Alerts/alerts";
import DashboardService from "../dashboard.service";
import AuthService from "../../Authentication/auth.service";
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import moment from 'moment';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

const OrderConfirmationModal = ({ isOpen, toggle, orderId, message }) => {
  return (
    <Modal isOpen={isOpen} toggle={toggle} centered className="order-confirmation-modal">
      <ModalBody className="text-center p-4">
        <div className="confirmation-icon mb-3">
          <FaCheck size={24} color="#fff" />
        </div>
        <h4 className="mb-2">{message}</h4>
        <h2 className="order-id mb-0">ID# {orderId}</h2>
      </ModalBody>
    </Modal>
  );
};

// Remove BatchSelectionModal and all batch modal logic

const EditRfq = () => {
  // State hooks
  const [items, setItems] = useState([]);
  const [allItems, setAllItems] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [orderId, setOrderId] = useState('');
  const [loading, setLoading] = useState(false);
  const [reason_of_use, setReason_of_use] = useState('');
  const [subtotal, setSubtotal] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [userInfo] = useState(() => JSON.parse(localStorage.getItem('user')));
  const [tooltipOpen, setTooltipOpen] = useState({});

  // Router and translation hooks
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { id } = useParams();

  // Helper to calculate item total
  const calculateItemTotal = useCallback((item) => (item.price || 0) * (item.quantity || 0), []);

  // Fetch all items on mount
  useEffect(() => {
    fetchAllItems();
    // eslint-disable-next-line
  }, []);

  // Calculate subtotal and totalAmount whenever items change
  useEffect(() => {
    const calculatedSubtotal = items.reduce((sum, item) => sum + calculateItemTotal(item), 0);
    setSubtotal(calculatedSubtotal);
    setTotalAmount(calculatedSubtotal);
  }, [items, calculateItemTotal]);

  // Fetch Order details after allItems are loaded
  useEffect(() => {
    if (allItems.length > 0) {
      fetchRfqDetails();
    }
    // eslint-disable-next-line
  }, [allItems]);

  // Memoized item options for Select
  const itemOptions = useMemo(() =>
    allItems
      .filter(catItem => catItem.status === 'Active')
      .map(catItem => ({
        value: catItem.value,
        label: catItem.label,
        category: catItem.category,
        ItemGroup: catItem.ItemGroup,
        status: catItem.status,
        price: catItem.price,
        item_code: catItem.item_code,
        BaseUOM: catItem.BaseUOM
      })),
    [allItems]
  );

  // Fetch all items
  const fetchAllItems = useCallback(async () => {
    setLoading(true);
    try {
      const getData = {
        functionName: "GetDropDownItems",
        postData: {
          sort: { _id: -1 },
          offset: 0,
          limit: 99999,
          portal: 'customers',
          filter: {},
          business: []
        },
      };
      const response = await DashboardService.getAllItems(getData);
      if (response.data.message === "Unauthorized") {
        AuthService.logout();
        navigate("/login");
      } else {
        const formattedItems = response.data.data.map(catItem => ({
          value: catItem._id.toString(),
          label: catItem.item_name,
          category: catItem.category,
          ItemGroup: catItem.ItemGroup,
          status: catItem.status,
          price: catItem.price,
          expiry: catItem.expiry,
          item_code: catItem.item_code || catItem.product_code || catItem.sku || catItem.code || catItem._id.toString(),
          BaseUOM: catItem.BaseUOM
        }));
        setAllItems(formattedItems);
      }
    } catch (error) {
      Alerts.swalErrorAlert(t("Error fetching items"));
    }
    setLoading(false);
  }, [navigate, t]);

  // Fetch batches for an item
  const fetchBatches = useCallback(async (item_identifier) => {
    try {
      const business_id = [];
      let response;
      try {
        response = await DashboardService.getBatchesForItem(item_identifier, business_id);
      } catch (error) {
        // If item_identifier is an ID (24 characters), try alternative approach
        if (item_identifier.length === 24) {
          // For now, return empty array if getBatchesForItemById doesn't exist
          return [];
        } else {
          throw error;
        }
      }
      return response.data || [];
    } catch (error) {
      console.error("Error fetching batches:", error);
      return [];
    }
  }, []);

  // Format batches for dropdown display
  const formatBatchesForDisplay = useCallback((batches) => {
    if (!batches || batches.length === 0) return [];
    return batches
      .filter(batch => batch.quantity > 0) // Only show batches with available stock
      .map(batch => ({
        batchId: batch._id || batch.batch_id,
        batch_number: batch.batch_number || batch.batchNumber,
        quantity: batch.quantity,
        expiry: batch.expiry_date || batch.expiry || batch.expiryDate,
        price: batch.price
      }))
      .sort((a, b) => new Date(a.expiry) - new Date(b.expiry)); // Sort by expiry date
  }, []);

  // Get nearest expiry from batches
  const getNearestExpiry = useCallback((batches) => {
    if (!batches || batches.length === 0) return '';
    // Only consider batches with quantity > 0 and an expiry field
    const validBatches = batches.filter(b => (b.quantity > 0) && (b.expiry_date || b.expiry || b.expiryDate));
    if (validBatches.length === 0) return '';
    validBatches.sort((a, b) => new Date(a.expiry_date || a.expiry || a.expiryDate) - new Date(b.expiry_date || b.expiry || b.expiryDate));
    return validBatches[0].expiry_date || validBatches[0].expiry || validBatches[0].expiryDate;
  }, []);

  // Fetch Order details
  const fetchRfqDetails = useCallback(async () => {
    setLoading(true);
    try {
      const payload = {
        functionName: "GetRFQDetails",
        postData: { _id: id },
      };
      const response = await DashboardService.GetRFQDetails(payload);
      if (response.data.status) {
        const rfqData = response.data.data[0];
        const prefillItems = await Promise.all(rfqData.items.map(async (item) => {
          let matchedItem = allItems.find(allItem => allItem.value === item.item.toString()) ||
            (item.item_name && allItems.find(allItem => allItem.label === item.item_name));
          const itemObj = {
            name: matchedItem
              ? {
                value: matchedItem.value,
                label: matchedItem.label,
                category: matchedItem.category,
                ItemGroup: matchedItem.ItemGroup,
                status: matchedItem.status,
                price: matchedItem.price,
                item_code: matchedItem.item_code,
                BaseUOM: matchedItem.BaseUOM
              }
              : {
                value: item.item.toString(),
                label: item.item_name || "Unknown Item",
                item_code: item.item_code,
                ItemGroup: item.ItemGroup,
                BaseUOM: item.BaseUOM
              },
            quantity: item.quantity,
            expiry: '',
            price: item.price || (matchedItem ? matchedItem.price : 0),
            item_total: item.item_total || 0,
            availableBatches: [],
            selectedBatches: [{
              batchId: '',
              batch_number: '',
              quantity: item.quantity || 1,
              expiry: '',
              price: item.price || (matchedItem ? matchedItem.price : 0)
            }]
          };
          if (itemObj.name?.item_code) {
            try {
              const batchResponse = await DashboardService.getBatchesForItem(itemObj.name.item_code, []);
              if (batchResponse && batchResponse?.data?.data) {
                const formattedBatches = formatBatchesForDisplay(batchResponse?.data?.data);
                itemObj.availableBatches = formattedBatches;
                itemObj.expiry = getNearestExpiry(batchResponse?.data?.data);
                // Set the first available batch as default if available
                if (formattedBatches.length > 0) {
                  itemObj.selectedBatches = [{
                    ...formattedBatches[0],
                    quantity: item.quantity || 1
                  }];
                }
              } else {
                itemObj.expiry = '';
                itemObj.availableBatches = [];
              }
            } catch {
              itemObj.expiry = '';
              itemObj.availableBatches = [];
            }
          }
          return itemObj;
        }));
        setItems(prefillItems);
        setOrderId(rfqData.rfq_id);
        setReason_of_use(rfqData.reason_of_use);
      } else {
        Alerts.swalErrorAlert(response.data.message || t("Failed to fetch Order details"));
      }
    } catch (error) {
      Alerts.swalErrorAlert(t("An error occurred while fetching Order details"));
    }
    setLoading(false);
  }, [allItems, id, t, getNearestExpiry, formatBatchesForDisplay]);

  // Add new item
  const handleAddItem = useCallback(() => {
    setItems(prev => ([
      ...prev,
      {
        name: null,
        quantity: 1,
        availableBatches: [],
        selectedBatches: [{
          batchId: '',
          batch_number: '',
          quantity: 1,
          expiry: '',
          price: 0
        }],
        price: 0,
        item_total: 0,
      },
    ]));
  }, []);

  // Handle item change
  const handleItemChange = useCallback(async (index, key, value) => {
    setItems(prevItems => {
      const newItems = [...prevItems];
      const oldItem = newItems[index];
      newItems[index] = { ...newItems[index], [key]: value };
      if (key === 'name') {
        newItems[index] = {
          ...oldItem,
          name: value,
          category: value.category,
          price: value.price || 0,
          availableBatches: [],
          selectedBatches: [{
            batchId: '',
            batch_number: '',
            quantity: oldItem.quantity || 1,
            expiry: '',
            price: value.price || 0
          }]
        };
        // Fetch batches for the selected item
        fetchBatches(value.item_code).then(batchData => {
          const formattedBatches = formatBatchesForDisplay(batchData.data || batchData);
          setItems(items => {
            const updated = [...items];
            updated[index].availableBatches = formattedBatches;
            // Set the first available batch as default if available
            if (formattedBatches.length > 0) {
              updated[index].selectedBatches = [formattedBatches[0]];
              updated[index].expiry = formattedBatches[0].expiry;
            } else {
              updated[index].expiry = '';
            }
            updated[index].item_total = calculateItemTotal(updated[index]);
            return updated;
          });
        }).catch(() => {
          setItems(items => {
            const updated = [...items];
            updated[index].availableBatches = [];
            updated[index].expiry = '';
            updated[index].item_total = calculateItemTotal(updated[index]);
            return updated;
          });
        });
      } else {
        newItems[index].item_total = calculateItemTotal(newItems[index]);
      }
      return newItems;
    });
  }, [calculateItemTotal, fetchBatches, formatBatchesForDisplay]);

  // Handle batch selection change
  const handleBatchChange = useCallback((itemIndex, batchIndex, selectedBatchId) => {
    setItems(prevItems => {
      const newItems = [...prevItems];
      const item = newItems[itemIndex];
      const selectedBatch = item.availableBatches.find(batch => batch.batchId === selectedBatchId);
      
      if (selectedBatch) {
        newItems[itemIndex].selectedBatches[batchIndex] = {
          ...selectedBatch,
          quantity: newItems[itemIndex].selectedBatches[batchIndex]?.quantity || 1
        };
        newItems[itemIndex].expiry = selectedBatch.expiry;
      }
      
      newItems[itemIndex].item_total = calculateItemTotal(newItems[itemIndex]);
      return newItems;
    });
  }, [calculateItemTotal]);

  // Remove item
  const handleRemoveItem = useCallback((index) => {
    setItems(prev => prev.filter((_, i) => i !== index));
  }, []);

  // Handle drag end
  const handleDragEnd = useCallback((result) => {
    if (!result.destination) {
      return;
    }

    const sourceIndex = result.source.index;
    const destinationIndex = result.destination.index;

    if (sourceIndex === destinationIndex) {
      return;
    }

    setItems(prevItems => {
      const newItems = Array.from(prevItems);
      const [reorderedItem] = newItems.splice(sourceIndex, 1);
      newItems.splice(destinationIndex, 0, reorderedItem);
      return newItems;
    });
  }, []);

  // Toggle tooltip
  const toggleTooltip = useCallback((index) => {
    setTooltipOpen(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  }, []);

  // Save Order data
  const saveRFQData = useCallback(async (rfqData) => {
    try {
      const response = await DashboardService.updateRFQ(rfqData);
      if (response.data.status) {
        return response.data;
      } else {
        throw new Error(response.data.message || "Failed to update Order");
      }
    } catch (error) {
      throw error;
    }
  }, []);

  // Submit handler
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (items.length === 0) {
        Alerts.swalErrorAlert(t("At least select 1 item."));
        return;
      }
      const validItems = items.map((item) => {
        if (!item?.name?.value || !item?.quantity || item.quantity < 1) return null;
        const isDoctor = location.state?.from === 'request_for_quotation';
        return {
          item: item.name.value,
          category: item.name?.category || item.category,
          quantity: item.quantity,
          price: isDoctor ? undefined : item.price,
          expiry: item.expiry,
          ItemGroup: item.name?.ItemGroup || item.ItemGroup || 'NP',
          item_total: calculateItemTotal(item),
        };
      }).filter(Boolean);
      if (validItems.length === 0) {
        Alerts.swalErrorAlert(t("Selected items are invalid. Please review and correct them."));
        return;
      }
      const rfqData = {
        _id: id,
        items: validItems,
        user_id: userInfo._id,
        reason_of_use: reason_of_use
      };
      await saveRFQData(rfqData);
      Alerts.swalSuccessAlert(t("Order updated successfully"));
      goBackAfterEdit();
    } catch (error) {
      Alerts.swalErrorAlert(t("Error updating Order."));
    } finally {
      setLoading(false);
    }
  };

  // Go back after edit
  const goBackAfterEdit = useCallback(() => {
    if (location.state?.from === 'Quotations') {
      navigate('/Quotations');
    } else if (location.state?.from === 'request_for_quotation') {
      navigate('/request_for_quotation');
    } else {
      navigate(-1);
    }
  }, [location.state, navigate]);

  // Render
  return (
    <div className="app-main__outer">
      <div className="container mt-4">
        <div className="mb-4">
          <Button color="link" className="p-0 text-decoration-none" onClick={() => navigate(-1)}>
            <FaArrowLeft className="me-2" />
            <span className="text-dark fw-bold">{t("Edit New Order")}</span>
          </Button>
        </div>
        <Card className="rfq-card">
          <div className="p-4">
            <h4 className="mb-4 fw-bold">{t("Edit Order")} #{orderId}</h4>
            <Form onSubmit={handleSubmit}>
              <LoadingOverlay active={loading} spinner={<DNALoader />}>
                <Col md={12}>
                  <Row className="mb-3 align-items-center">
                    <Col md={2}>
                      <h5 className="fw-bold">{t("Reason of Use")}</h5>
                    </Col>
                    <Col md={4}>
                      <Input
                        type="text"
                        value={reason_of_use}
                        onChange={(e) => setReason_of_use(e.target.value)}
                      />
                    </Col>
                  </Row>
                </Col>
                <h5 className="mb-3 fw-bold">{t("Edit Items")}</h5>
                
                {/* Unified Items and Batch Table */}
                <div className="unified-table-container mb-4">
                  <DragDropContext onDragEnd={handleDragEnd}>
                    <Table responsive className="unified-table">
                      <thead>
                        <tr>
                          <th style={{ width: '3%' }}></th>
                          <th style={{ width: '20%' }}>{t("Item")}</th>
                          <th style={{ width: '8%' }}>{t("Quantity")}</th>
                          <th style={{ width: '7%' }}>{t("UOM")}</th>
                          <th style={{ width: '8%' }}>{t("Price (RM)")}</th>
                          <th style={{ width: '18%' }}>{t("Batch Selection")}</th>
                          <th style={{ width: '10%' }}>{t("Available Stock")}</th>
                          <th style={{ width: '10%' }}>{t("Expiry Date")}</th>
                          <th style={{ width: '9%' }}>{t("Total (RM)")}</th>
                          <th style={{ width: '7%' }}>{t("Action")}</th>
                        </tr>
                      </thead>
                      <Droppable droppableId="items-list">
                        {(provided, snapshot) => (
                          <tbody
                            {...provided.droppableProps}
                            ref={provided.innerRef}
                            className={snapshot.isDraggingOver ? 'dragging-over' : ''}
                          >
                            {items.map((item, itemIndex) => (
                              <Draggable
                                key={`item-${itemIndex}`}
                                draggableId={`item-${itemIndex}`}
                                index={itemIndex}
                              >
                                {(provided, snapshot) => (
                                  <tr
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    className={snapshot.isDragging ? 'dragging' : ''}
                                  >
                                    <td className="text-center drag-handle" {...provided.dragHandleProps}>
                                      <FaGripVertical className="grip-icon" />
                                    </td>
                                    <td className="text-center">
                                      <div id={`tooltip-item-${itemIndex}`}>
                                        <Select
                                          value={item.name}
                                          onChange={(selectedOption) => handleItemChange(itemIndex, 'name', selectedOption)}
                                          options={itemOptions}
                                          placeholder={t("Select Item")}
                                          isSearchable
                                          className="item-select"
                                          menuPortalTarget={document.body}
                                          styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                                        />
                                      </div>
                                      {item.name && (
                                        <Tooltip
                                          placement="top"
                                          isOpen={tooltipOpen[itemIndex] || false}
                                          target={`tooltip-item-${itemIndex}`}
                                          toggle={() => toggleTooltip(itemIndex)}
                                        >
                                          {item.name.label}
                                        </Tooltip>
                                      )}
                                    </td>
                                    <td className="text-center">
                                      <Input
                                        type="number"
                                        min="1"
                                        value={item.quantity}
                                        onChange={(e) => handleItemChange(itemIndex, 'quantity', parseInt(e.target.value))}
                                        className="quantity-input"
                                      />
                                    </td>
                                    <td className="text-center">
                                      <Input
                                        type="text"
                                        value={item.name?.BaseUOM || ''}
                                        readOnly
                                        className="form-control-plaintext"
                                      />
                                    </td>
                                    <td className="text-center">
                                      <Input
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        value={item.price || 0}
                                        onChange={(e) => handleItemChange(itemIndex, 'price', parseFloat(e.target.value) || 0)}
                                        disabled={true}
                                        className="price-input"
                                      />
                                    </td>
                                    <td className="text-center">
                                      {item.availableBatches && item.availableBatches.length > 0 ? (
                                        <Input
                                          type="select"
                                          value={item.selectedBatches[0]?.batchId || ''}
                                          onChange={(e) => handleBatchChange(itemIndex, 0, e.target.value)}
                                          className="batch-select"
                                        >
                                          <option value="">{t("Select Batch")}</option>
                                          {item.availableBatches.map(availableBatch => (
                                            <option key={availableBatch.batchId} value={availableBatch.batchId}>
                                              {availableBatch.batch_number}
                                            </option>
                                          ))}
                                        </Input>
                                      ) : (
                                        <span className="text-muted">{t("No batches available")}</span>
                                      )}
                                    </td>
                                    <td className="text-center">
                                      {item.selectedBatches[0]?.batchId ? (
                                        <span className="stock-info">
                                          {item.availableBatches.find(b => b.batchId === item.selectedBatches[0]?.batchId)?.quantity || 0}
                                        </span>
                                      ) : (
                                        <span className="text-muted">--</span>
                                      )}
                                    </td>
                                    <td className="text-center">
                                      {item.selectedBatches[0]?.expiry ? (
                                        <span className="expiry-info">
                                          {moment(item.selectedBatches[0].expiry).format('DD/MM/YYYY')}
                                        </span>
                                      ) : (
                                        <span className="text-muted">--</span>
                                      )}
                                    </td>
                                    <td className="text-center">
                                      <span className="fw-bold total-amount">
                                        RM {calculateItemTotal(item).toFixed(2)}
                                      </span>
                                    </td>
                                    <td className="text-center">
                                      <Button 
                                        color="link" 
                                        className="p-0 text-danger remove-btn" 
                                        onClick={() => handleRemoveItem(itemIndex)}
                                      >
                                        <FaTrash />
                                      </Button>
                                    </td>
                                  </tr>
                                )}
                              </Draggable>
                            ))}
                            {provided.placeholder}
                          </tbody>
                        )}
                      </Droppable>
                    </Table>
                  </DragDropContext>
                </div>
                <div className="order-summary mb-4">
                  <div className="p-3 border rounded">
                    <h5 className="mb-3 fw-bold">{t("Order Summary")}</h5>
                    <div className="d-flex justify-content-between mb-2">
                      <span>{t("Subtotal")}:</span>
                      <span>RM {subtotal.toFixed(2)}</span>
                    </div>
                    <hr />
                    <div className="d-flex justify-content-between fw-bold">
                      <span>{t("Total")}:</span>
                      <span>RM {totalAmount.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
                <div className="d-flex">
                  <Button type="button" color="light" className="me-2 px-3" onClick={handleAddItem}>
                    <FaPlus className="me-2" /> {t("Add New")}
                  </Button>
                  <Button type="submit" color="primary" className="px-3">
                    <FaCheck className="me-2" /> {t("Update")}
                  </Button>
                </div>
              </LoadingOverlay>
            </Form>
          </div>
        </Card>
        <OrderConfirmationModal
          isOpen={modalOpen}
          toggle={() => setModalOpen(!modalOpen)}
          orderId={orderId}
          message="Order Updated"
        />
      </div>
      <style>{`
        .app-main__outer {
          background-color: #f8f9fa;
          min-height: 100vh;
          padding-top: 20px;
        }
        .rfq-card {
          border-radius: 10px;
          box-shadow: 0 4px 15px rgba(0,0,0,0.05);
          border: none;
        }
        .form-select, .form-control {
          border-color: #e0e0e0;
          border-radius: 5px;
          padding: 10px;
        }
        .btn-light {
          background-color: #ffffff;
          border-color: #e0e0e0;
          color: #333;
          border-radius: 6px;
        }
        .btn-primary {
          background-color: #3D607A;
          border-color: #3D607A;
          border-radius: 6px;
        }
        .btn-primary:hover {
          background-color: #2d4a5c;
          border-color: #2d4a5c;
        }
        .btn-outline-primary {
          border-color: #3D607A;
          color: #3D607A;
          border-radius: 6px;
        }
        .btn-outline-primary:hover:not(:disabled) {
          background-color: #3D607A;
          border-color: #3D607A;
        }
        .btn-outline-success {
          border-color: #28a745;
          color: #28a745;
          border-radius: 6px;
        }
        .btn-outline-success:hover:not(:disabled) {
          background-color: #28a745;
          border-color: #28a745;
        }
        .btn-secondary {
          background-color: #6c757d;
          border-color: #6c757d;
          border-radius: 6px;
        }
        .btn-secondary:hover:not(:disabled) {
          background-color: #5a6268;
          border-color: #5a6268;
        }
        .btn-danger {
          background-color: #dc3545;
          border-color: #dc3545;
          border-radius: 6px;
        }
        .btn-danger:hover:not(:disabled) {
          background-color: #c82333;
          border-color: #c82333;
        }
        .alert-info {
          background-color: #d1ecf1;
          border-color: #bee5eb;
          color: #0c5460;
        }
        .alert-info .fas {
          color: #0c5460;
        }
        .gap-2 {
          gap: 0.5rem !important;
        }
        .text-muted {
          color: #6c757d !important;
        }
        h4, h5 {
          color: #333;
        }
        .item-list {
          border-top: 1px solid #e0e0e0;
          border-bottom: 1px solid #e0e0e0;
          padding: 20px 0;
          background-color: #fcfcfc;
          border-radius: 8px;
        }
        .order-summary {
          margin-top: 24px;
        }
        .order-summary .border {
          border-radius: 8px;
        }
        .order-confirmation-modal {
          display: flex !important;
          align-items: center;
          justify-content: center;
        }
        .order-confirmation-modal .modal-content {
          border-radius: 20px;
          border: none;
          box-shadow: 0 5px 15px rgba(0,0,0,0.1);
        }
        .order-confirmation-modal .modal-body {
          padding: 2rem;
        }
        .confirmation-icon {
          width: 60px;
          height: 60px;
          background-color: #4CAF50;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 1.5rem;
        }
        .order-id {
          font-size: 2rem;
          font-weight: bold;
        }
        .mb-2.align-items-center {
          background-color: white;
          border-radius: 8px;
          padding: 12px 8px;
          margin-bottom: 12px !important;
          box-shadow: 0 2px 5px rgba(0,0,0,0.03);
          transition: all 0.2s;
        }
        .mb-2.align-items-center:hover {
          box-shadow: 0 3px 10px rgba(0,0,0,0.08);
        }
        .mb-3.fw-bold {
          border-left: 4px solid #3D607A;
          padding-left: 10px;
          margin-top: 20px;
        }
        .quantity-input {
          border-radius: 6px;
          text-align: center;
        }
        .unified-table-container {
          background-color: white;
          border-radius: 8px;
          padding: 20px;
          box-shadow: 0 2px 5px rgba(0,0,0,0.05);
          border: 1px solid #e0e0e0;
        }
        .unified-table {
          margin-bottom: 0;
        }
        .unified-table thead th {
          background-color: #3D607A;
          color: white;
          border: none;
          font-weight: 600;
          padding: 12px 8px;
          text-align: center;
          vertical-align: middle;
        }
        .unified-table tbody tr {
          border-bottom: 1px solid #e0e0e0;
        }
        .unified-table tbody tr:hover {
          background-color: #f8f9fa;
        }
        .unified-table tbody td {
          padding: 12px 8px;
          vertical-align: middle;
          border: none;
        }
        .item-select {
          min-width: 180px;
          position: relative;
          z-index: 100;
        }
        .item-select > div {
          z-index: 100 !important;
        }
        .item-select div[class*="-menu"] {
          z-index: 9999 !important;
        }
        .quantity-input, .price-input, .batch-quantity-input {
          width: 100%;
          text-align: center;
          border-radius: 4px;
          border: 1px solid #ddd;
        }
        .batch-select {
          width: 100%;
          border-radius: 4px;
          border: 1px solid #ddd;
        }
        .stock-info {
          color: #28a745;
          font-weight: 600;
        }
        .expiry-info {
          color: #dc3545;
          font-weight: 600;
        }
        .total-amount {
          color: #3D607A;
          font-size: 14px;
        }
        .remove-btn {
          color: #dc3545 !important;
        }
        .remove-btn:hover {
          color: #c82333 !important;
        }
        .text-muted {
          color: #6c757d !important;
          font-style: italic;
        }
        .drag-handle {
          cursor: grab;
          color: #6c757d;
        }
        .drag-handle:active {
          cursor: grabbing;
        }
        .grip-icon {
          font-size: 16px;
          opacity: 0.5;
          transition: opacity 0.2s;
        }
        .drag-handle:hover .grip-icon {
          opacity: 1;
        }
        .dragging {
          background-color: #f0f8ff !important;
          box-shadow: 0 5px 15px rgba(0,0,0,0.2);
          opacity: 0.9;
        }
        .dragging-over {
          background-color: #f8f9fa;
        }
        tbody tr {
          transition: background-color 0.2s;
        }
        .tooltip {
          font-size: 12px;
        }
        .tooltip-inner {
          max-width: 300px;
          text-align: left;
          background-color: #333;
          color: #fff;
          border-radius: 4px;
          padding: 8px 12px;
        }
      `}</style>
    </div>
  );
};

export default EditRfq;
